import moment from 'moment'
import { Segment } from 'redux/employer/employerTypes'
import {
  availableGrhMerchant,
  availableMicromobilityMerchant,
  availableFlexMerchant,
  SegmentListType,
} from '../Shared/types'

/*
  This super program is a combination of all the programs that are selected
  it is coming from the backend, so the structure is not exactly what we want
  we need to prepare it for the editing page
*/
export const prepareSuperProgramForEditing = (
  superProgram: any,
  allSegments: Segment[],
  availableGrhMerchant: availableGrhMerchant,
  availableMicromobilityMerchant: availableMicromobilityMerchant,
  availableFlexMerchant: availableFlexMerchant,
  allTags: { id: string; name: string }[] = []
) => {
  const {
    name,
    grh,
    qtf,
    micromobility,
    flex,
    transitPass,
    coupon,
    reportedUsage,
    startDate,
    segments,
    cancelledSegments,
    programTypes,
    status,
    launchId,
    programIds,
    currency,
    description,
  } = superProgram
  return {
    selectedSegments: correctSegmentList(segments, allSegments, allTags),
    cancelledSegments: correctSegmentList(cancelledSegments, allSegments, allTags),
    selectedDate: correctSelectedDate(startDate),
    selectedPrograms: programTypes,
    grh: correctGrh(grh, availableGrhMerchant),
    qtf: correctQtf(qtf),
    micromobility: correctMicromobility(
      micromobility,
      availableMicromobilityMerchant
    ),
    flex: correctFlex(flex, availableFlexMerchant),
    transitPass: correctTransitPass(transitPass),
    coupon: correctCoupon(coupon),
    reportedUsage: correctReportedUsage(reportedUsage),
    name,
    status,
    launchId,
    programIds,
    currency: currency || 'USD',
    description:
      description ||
      flex?.description ||
      transitPass?.description ||
      qtf?.description ||
      '',
  }
}

// For segment display we need it in type SegmentListType[]
// segments here is a array of ids
export const correctSegmentList = (
  segments: string[],
  allSegments: Segment[],
  allTags: { id: string; name: string }[] = []
) => {
  if (!segments || segments.length === 0) {
    return []
  }
  return segments.map((scopeId) => {
    const segmentObject = findSegment(scopeId, allSegments)
    if (segmentObject) {
      return {
        id: scopeId,
        text: segmentObject.name,
        type: segmentObject.type,
      }
    }
    const tag = allTags.find((t) => t.id === scopeId)
    if (tag) {
      return {
        id: scopeId,
        text: tag.name,
        type: 'TAG',
      }
    }
    return {
      id: scopeId,
      text: 'Unknown',
      type: 'unknown',
    }
  })
}

export const getScopeSelectionLabel = (
  segments: SegmentListType[],
  cancelled = false
): string => {
  const prefix = cancelled ? 'Cancelled' : 'Selected'
  const hasTags = segments.some((segment) => segment.type === 'TAG')
  const hasGroups = segments.some((segment) => segment.type !== 'TAG')

  if (hasTags && !hasGroups) {
    return segments.length === 1 ? `${prefix} Tag` : `${prefix} Tags`
  }
  if (hasGroups && !hasTags) {
    return segments.length === 1 ? `${prefix} Group` : `${prefix} Groups`
  }
  return `${prefix} Groups & Tags`
}

export const findSegment = (segmentId: string, allSegments: Segment[]) => {
  const segment = allSegments.find(
    (segment: Segment) => segment.id === segmentId
  )
  return segment || null
}

// We need to convert the startDate to a Month object
const correctSelectedDate = (startDate: string) => {
  if (!startDate) {
    return {
      id: moment().toDate().toISOString(),
      text: moment().format('MMMM Do, YYYY'),
      iso: moment().toDate().toISOString(),
    }
  }
  return {
    id: startDate,
    text: moment(startDate).format('MMMM Do, YYYY'),
    iso: startDate,
  }
}

const getMerchantIdsArray = (
  merchantIds: string[],
  availableMerchant: any,
  type: string
) => {
  try {
    const merchantIdsArray = []
    availableMerchant[type].filter((merchant) => {
      if (merchantIds.includes(merchant.id)) {
        merchantIdsArray.push(merchant.id)
      }
      return null
    })
    return merchantIdsArray
  } catch (error) {
    return []
  }
}

const checkIfMerchantIsAvailable = (
  merchantIds: string[],
  availableMerchant: any,
  type: string
) => {
  try {
    const availableMerchants = getMerchantIdsArray(
      merchantIds,
      availableMerchant,
      type
    )
    if (availableMerchants.length > 0) {
      return true
    }
    return false
  } catch (error) {
    return false
  }
}

const correctGrh = (grh: any, availableGrhMerchant: availableGrhMerchant) => {
  if (!grh) {
    return {}
  }
  return {
    transaction: grh.budget,
    uses: grh.numUses,
    activeOptions: {
      rideshare: checkIfMerchantIsAvailable(
        grh.merchants,
        availableGrhMerchant,
        'rideshare'
      ),
      localTaxi: checkIfMerchantIsAvailable(
        grh.merchants,
        availableGrhMerchant,
        'localTaxi'
      ),
      governmentGrh: grh.governmentGrh,
    },
    activeMerchantIds: {
      rideshare: getMerchantIdsArray(
        grh.merchants,
        availableGrhMerchant,
        'rideshare'
      ),
      localTaxi: getMerchantIdsArray(
        grh.merchants,
        availableGrhMerchant,
        'localTaxi'
      ),
    },
  }
}

const correctQtf = (qtf: any) => {
  if (!qtf) {
    return {}
  }
  return {
    payrollSchedule: qtf.payrollCycle,
    benefits: {
      transit: qtf.type.includes('COMMUTER_TRANSIT'),
      parking: qtf.type.includes('COMMUTER_PARKING'),
    },
    employerContributions: {
      transit: qtf.employerTransitContribution,
      parking: qtf.employerParkingContribution,
    },
    finalizeAllocation: qtf.contributionDeadlineDay,
    maxAllocationLimit: {
      transit: qtf.employerTransitMaxAllocationLimit ?? null,
      parking: qtf.employerParkingMaxAllocationLimit ?? null,
    },
  }
}

const correctMicromobility = (
  micromobility: any,
  availableMicromobilityMerchant: availableMicromobilityMerchant
) => {
  if (!micromobility) {
    return {}
  }
  return {
    budget: micromobility.budget,
    prorated: micromobility.prorated ?? false,
    activeOptions: {
      leasing: micromobility.leasing,
      shared: micromobility.shared,
      oneTime: checkIfMerchantIsAvailable(
        micromobility.merchants,
        availableMicromobilityMerchant,
        'oneTime'
      ),
    },
    activeMerchantIds: {
      leasing: getMerchantIdsArray(
        micromobility.merchants,
        availableMicromobilityMerchant,
        'leasing'
      ),
      shared: getMerchantIdsArray(
        micromobility.merchants,
        availableMicromobilityMerchant,
        'shared'
      ),
      oneTime: getMerchantIdsArray(
        micromobility.merchants,
        availableMicromobilityMerchant,
        'oneTime'
      ),
    },
  }
}

const correctFlex = (
  flex: any,
  availableFlexMerchant: availableFlexMerchant
) => {
  if (!flex) {
    return {}
  }
  return {
    budget: flex.budget,
    budgetCycle: flex.budgetCycle,
    oneTimePurchase: flex.oneTimePurchase ?? false,
    rolloverBudget: flex.rolloverBudget ?? false,
    prorated: flex.prorated ?? false,
    activeOptions: {
      flex: checkIfMerchantIsAvailable(
        flex.merchants,
        availableFlexMerchant,
        'flex'
      ),
      gas: flex.gas,
      transit: flex.transit,
      parking: flex.parking,
      tolls: flex.tolls ?? false,
      rideshare: flex.rideshare ?? false,
      carshare: flex.carshare ?? false,
      transportationServices: flex.transportationServices ?? false,
    },
    activeMerchantIds: {
      flex: getMerchantIdsArray(flex.merchants, availableFlexMerchant, 'flex'),
      gas: [],
      transit: [],
      parking: [],
      tolls: [],
      rideshare: [],
      carshare: [],
      transportationServices: [],
    },
  }
}

const correctTransitPass = (transitPass: any) => {
  if (!transitPass) {
    return {}
  }
  const transitAgencyIds = (transitPass.transitAgencies || []).map(
    (agency: string | { id: string }) =>
      typeof agency === 'string' ? agency : agency.id
  )
  return {
    purchaseType: transitPass.purchaseType || 'EMPLOYEE_CHOICE',
    walletTopUpEnabled: transitPass.walletTopUpEnabled ?? false,
    budget: transitPass.budget ?? 0,
    passDeliveryMechanism: transitPass.passDeliveryMechanism || 'PHYSICAL',
    agenciesEnabled: transitAgencyIds.length > 0,
    transitAgencyIds,
    availableTransitAgencies: [],
  }
}

const correctReportedUsage = (program: any) => {
  if (!program) {
    return { merchants: [] }
  }
  return {
    merchants: program.reportedMerchants || [],
  }
}

const correctCoupon = (coupon: any) => {
  if (!coupon) {
    return {}
  }
  const merchantIds = (coupon.merchants || []).map(
    (merchant: string | { id: string }) =>
      typeof merchant === 'string' ? merchant : merchant.id
  )
  return {
    merchantsEnabled: merchantIds.length > 0,
    merchantIds,
    availableMerchants: [],
    couponCode: coupon.couponCode || '',
  }
}
